/**
 * SSE connection manager for real-time task and notification events.
 *
 * The server uses a single-use ticket (minted via POST /api/stream/ticket)
 * as auth because EventSource cannot send custom headers.
 *
 * Public API:
 *   startStream({ projects, onEvent }) => stop
 *
 * Known event types (others are silently ignored):
 *   task.created  – data: { id, projectId }
 *   task.updated  – data: { id, projectId }
 *   task.status   – data: { id, projectId }
 *   notification.created – data: { id, type, taskId }
 *
 * ACHTUNG beim Aendern: der Name muss WOERTLICH dem entsprechen, was
 * `apps/server/src/lib/notify.ts` publiziert. Bis 2026-08-21 stand hier
 * "notification", der Server sendet aber "notification.created" — die Glocke
 * hat live also nie aktualisiert, ohne dass es auffiel (der Feed wurde
 * ohnehin beim Navigieren neu geladen).
 */

import { mintStreamTicket } from "./api";

// ---------- pure helpers (exported for unit tests) ----------

/** Maximum reconnect delay in milliseconds. */
export const MAX_BACKOFF_MS = 30_000;

/** Base delay for the first retry in milliseconds. */
export const BASE_BACKOFF_MS = 1_000;

/**
 * Compute the next reconnect delay using truncated exponential back-off.
 *
 * @param attempt  Zero-based reconnect attempt index (0 = first retry).
 * @param jitter   Optional function returning a value in [0, 1) for jitter;
 *                 defaults to Math.random. Pass () => 0 for deterministic tests.
 * @returns Delay in milliseconds, capped at MAX_BACKOFF_MS.
 */
export function nextBackoff(attempt: number, jitter: () => number = Math.random): number {
  const exp = Math.min(attempt, 10); // prevent BigInt overflow on Math.pow
  const base = BASE_BACKOFF_MS * Math.pow(2, exp);
  const capped = Math.min(base, MAX_BACKOFF_MS);
  return Math.floor(capped * (1 + jitter()) / 2);
}

/**
 * Build the EventSource URL query string.
 *
 * @param ticket   Single-use auth ticket from the server.
 * @param projects List of project ids to subscribe to. Empty = user channel only.
 * @returns Full path + query string, e.g. "/api/stream?ticket=abc&projects=1,2".
 */
export function buildStreamUrl(ticket: string, projects: number[]): string {
  const params = new URLSearchParams({ ticket });
  if (projects.length > 0) {
    params.set("projects", projects.join(","));
  }
  return `/api/stream?${params.toString()}`;
}

// ---------- known event type union ----------

export type SseEventType =
  | "task.created"
  | "task.updated"
  | "task.status"
  | "notification.created";

export interface TaskEventData {
  id: number;
  projectId: number;
}

export interface NotificationEventData {
  id: number;
  type: string;
  taskId?: number;
}

export type SseEventData = TaskEventData | NotificationEventData;

// ---------- connection manager ----------

export interface StartStreamOptions {
  /** Project ids to subscribe to in addition to the user channel. */
  projects: number[];
  /** Called for every recognised event. */
  onEvent: (type: SseEventType, data: SseEventData) => void;
}

/** Returned from startStream — call to permanently close the connection. */
export type StopFn = () => void;

export const KNOWN_TYPES: ReadonlySet<string> = new Set<SseEventType>([
  "task.created",
  "task.updated",
  "task.status",
  "notification.created",
]);

/**
 * Open a streaming connection to the server and keep it alive.
 *
 * Returns a stop() function. Calling stop() permanently closes the connection
 * and prevents any further reconnect attempt.
 *
 * Safe to call in non-browser environments (SSR / test with EventSource
 * removed): returns a no-op stop() if EventSource is not available.
 */
export function startStream(opts: StartStreamOptions): StopFn {
  // Guard: EventSource not available (Node, happy-dom without polyfill, etc.)
  if (typeof EventSource === "undefined") {
    return () => { /* no-op */ };
  }

  let stopped = false;
  let es: EventSource | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  // Pending "connection proved stable" timer — see onopen below.
  let stableTimer: ReturnType<typeof setTimeout> | null = null;

  async function connect(): Promise<void> {
    if (stopped) return;

    let ticket: string;
    try {
      ticket = await mintStreamTicket();
    } catch {
      // Ticket mint failed (likely network/auth) — back off and retry.
      scheduleReconnect();
      return;
    }

    if (stopped) return;

    const url = buildStreamUrl(ticket, opts.projects);
    const source = new EventSource(url);
    es = source;

    for (const type of KNOWN_TYPES) {
      source.addEventListener(type, (e: Event) => {
        const msgEvent = e as MessageEvent;
        let data: unknown;
        try {
          data = JSON.parse(msgEvent.data as string);
        } catch {
          return; // malformed JSON — skip silently
        }
        opts.onEvent(type as SseEventType, data as SseEventData);
      });
    }

    source.onerror = () => {
      if (stableTimer) {
        clearTimeout(stableTimer);
        stableTimer = null;
      }
      source.close();
      es = null;
      if (!stopped) {
        scheduleReconnect();
      }
    };

    // Reset the attempt counter only after the connection has stayed open for
    // a few seconds. `open` fires the moment the HTTP stream is accepted, so a
    // flapping proxy (open→error→open→error) would otherwise reset the counter
    // every cycle and defeat the backoff, hammering the server ~1×/s forever.
    source.onopen = () => {
      if (stableTimer) clearTimeout(stableTimer);
      stableTimer = setTimeout(() => {
        stableTimer = null;
        attempt = 0;
      }, 5000);
    };
  }

  function scheduleReconnect(): void {
    if (stopped) return;
    const delay = nextBackoff(attempt);
    attempt += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void connect();
    }, delay);
  }

  void connect();

  return function stop(): void {
    stopped = true;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    if (stableTimer !== null) {
      clearTimeout(stableTimer);
      stableTimer = null;
    }
    if (es) {
      es.close();
      es = null;
    }
  };
}
